const configFrontend = require("../../backend/config/frontend.json");

const isValidVAT = (value) => {
  if (!value) return false;
  const cleanedBTW = cleanVAT(value);
  // Define regex patterns for Benelux countries and France, Germany
  const patterns = {
    BE: /^BE[0-9]{10}$/, // Belgium: 'BE' followed by 10 digits
    NL: /^NL[0-9]{9}B[0-9]{2}$/, // Netherlands: 'NL' followed by 9 digits, 'B', and 2 digits
    LU: /^LU[0-9]{8}$/, // Luxembourg: 'LU' followed by 8 digits
    FR: /^FR[A-Z0-9]{2}[0-9]{9}$/, // France: 'FR' followed by 2 alphanumeric characters and 9 digits
    DE: /^DE[0-9]{9}$/, // Germany: 'DE' followed by 9 digits
  };

  // Extract the country code (first two characters)
  const countryCode = cleanedBTW.slice(0, 2);

  // Validate the BTW number against the pattern for the specific country
  if (patterns[countryCode] && patterns[countryCode].test(cleanedBTW)) {
    return true;
  }

  return false;
};

const isValidKBO = (kbo) => {
  if (!kbo) return false;
  const cleanedKBO = cleanKBO(kbo);
  const regex = /^[01]\d{3}\.\d{3}\.\d{3}$/;
  return regex.test(cleanedKBO);
};

const cleanKBO = (kbo) => {
  if (!kbo) return "";
  const kboWithoutDotsAndLetters = kbo.replace(/[a-zA-Z]/g, "");
  const kboWithDots = kboWithoutDotsAndLetters.replace(
    /(\d{4})(\d{3})(\d{3})/,
    "$1.$2.$3"
  );
  if (kboWithDots.startsWith("BE")) {
    return kboWithDots;
  }
  return kboWithDots;
};

const generatePossibleBelgianVATFromKBO = (kbo) => {
  const possibleVAT = `BE${kbo}`;
  const cleanedPossibleVAT = cleanVAT(possibleVAT);
  return cleanedPossibleVAT;
};

const cleanVAT = (value) => {
  if (!value) return "";
  return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
};

const formatVAT = (vatNumber) => {
  // Zorg ervoor dat het begint met BE
  if (!vatNumber.startsWith("BE")) {
    // throw new Error("Invalid VAT number format. Must start with 'BE'.");
    return vatNumber;
  }

  // Haal alle niet-numerieke karakters eruit behalve de eerste twee (BE)
  let numericPart = vatNumber.slice(2).replace(/\D/g, "");

  // Combineer de BE-prefix met de opgeschoonde numerieke waarde
  return `BE${numericPart}`;
};

function formatVATNumber(vatNumber) {
  // Verwijder alle niet-numerieke karakters behalve de eerste twee letters (landcode)
  if (vatNumber) {
    let match = vatNumber.match(/^([A-Z]{2})?(\d{9,10})$/);
    if (!match) return null; // Ongeldig BTW-nummer

    let countryCode = match[1] || "BE"; // Standaard naar BE als geen landcode
    let numbers = match[2];

    if (numbers.length === 10) {
      return `${countryCode}${numbers.slice(0, 4)}.${numbers.slice(4, 7)}.${numbers.slice(7)}`;
    } else {
      return `${countryCode}${numbers}`;
    }
  }
  return null;
}

function formatRegionId(region) {
  // Format the "id" as "RXXX" with leading zeros
  return `R${String(region.id).padStart(3, "0")}`;
}

function formatRegionCode(regionNumber) {
  // Format the "id" as "RXXX" with leading zeros
  return `R${String(regionNumber).padStart(3, "0")}`;
}

function buildRegionLookup(regios) {
  const exactMap = new Map();   // "9040" -> regionId
  const prefixMap = new Map();  // "90" / "904" -> regionId

  function addCodes(codes, regionId) {
    for (const raw of codes || []) {
      const str = String(raw).trim();

      // exact
      if (str.length === 4) {
        exactMap.set(str, regionId);
      }

      // prefixes
      if (str.length >= 2) {
        prefixMap.set(str.slice(0, 2), regionId);
      }
      if (str.length >= 3) {
        prefixMap.set(str.slice(0, 3), regionId);
      }
    }
  }

  for (const region of regios) {
    addCodes(region.postalcodes, region.id);

    for (const sub of region.subregios || []) {
      addCodes(sub.postalcodes, region.id);
    }
  }

  return { exactMap, prefixMap };
}

// build once
const { exactMap, prefixMap } = buildRegionLookup(configFrontend.regios);

function findRegionByPostalCode(postalcode) {
  if (!postalcode) {
    return { regionNumber: null, regionCode: null };
  }

  const code = String(postalcode).trim();

  const regionNumber =
    exactMap.get(code) ??
    prefixMap.get(code.slice(0, 3)) ??
    prefixMap.get(code.slice(0, 2)) ??
    null;

  return {
    regionNumber,
    regionCode: regionNumber ? formatRegionCode(regionNumber) : null,
  };
}

function findIdForPostalCode(postalCode) {
  // Iterate through each region in the "regios" array
  const regios = configFrontend.regios;
  for (const region of regios) {
    // Iterate through the "postalcodes" array of the region
    for (const code of region.postalcodes) {
      if (code.toString().length === 4 && code == postalCode) {
        // 4-digit postal code found, return the corresponding "id" formatted as "RXXX"
        return formatRegionId(region);
      }
    }
  }
  // Return a value (e.g., null) to indicate that no matching region was found
  return null;
}

module.exports = {
  cleanVAT,
  cleanKBO,
  isValidKBO,
  isValidVAT,
  formatVAT,
  formatVATNumber,
  findRegionByPostalCode,
  findIdForPostalCode,
  formatRegionId,
  generatePossibleBelgianVATFromKBO,
};
